import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Message from './Message';
import CardAutocomplete from './CardAutocomplete';
import UpgradeToPremium from './UpgradeToPremium';
import { theme, buttonStyles, inputStyles, spacing, mergeStyles, isMobile, mobileSpacing, typography, borderRadius } from '../styles/theme';
import { Sentry } from '../sentry';
import { trackUserEvent } from '../analytics';
import { exportConversationToPDF } from '../utils/exportPDF';

export default function Chat({
  user,
  userData,
  currentConversationId,
  onConversationChange,
  onConversationCreated
}) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [cursorPosition, setCursorPosition] = useState(0);
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [usageWarning, setUsageWarning] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [limitError, setLimitError] = useState(null);
  const [conversationId, setConversationId] = useState(currentConversationId);
  const [isSaving, setIsSaving] = useState(false);
  const [conversationTitle, setConversationTitle] = useState(null);
  const [showBracketTip, setShowBracketTip] = useState(false);
  const [bracketTipDismissed, setBracketTipDismissed] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // This scrolls the chat to the bottom whenever messages change
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Run scrollToBottom whenever messages array changes
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Clear usage warning when user changes (login/logout)
  useEffect(() => {
    setUsageWarning(null);
    setLimitError(null);
  }, [user]);

  // Load conversation when ID changes
  useEffect(() => {
    if (currentConversationId && user && userData?.isPremium) {
      loadConversation(currentConversationId);
    } else if (!currentConversationId) {
      // New conversation - clear messages
      setMessages([]);
      setConversationId(null);
      setConversationTitle(null);
    }
  }, [currentConversationId, user, userData?.isPremium]);

  useEffect(() => {
    const dismissed = localStorage.getItem('bracketTipDismissed');
    const questionCount = parseInt(localStorage.getItem('questionCount') || '0');

    // Show tip if not dismissed and asked fewer than 3 questions
    if (dismissed !== true && questionCount < 3) {
      setShowBracketTip(true);
    }
  }, []);

  // Function to load a conversation
  const loadConversation = async (convId) => {
    try {
      const token = await user.getIdToken();
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/conversations/${convId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Convert to message format
      const loadedMessages = response.data.messages.map(msg => ({
        sender: msg.role === 'user' ? 'user' : 'bot',
        text: msg.content
      }));
      
      setMessages(loadedMessages);
      setConversationId(convId);
      setConversationTitle(response.data.title);
    } catch (error) {
      console.error('Error loading conversation:', error);
      Sentry.captureException(error, {
        tags: { component: 'Chat', action: 'loadConversation' }
      });
    }
  };  

  // Track cursor position
  const handleInputChange = (e) => {
    setInput(e.target.value);
    setCursorPosition(e.target.selectionStart);

    const textarea = e.target;
    textarea.style.height = 'auto'; // Reset height
    const newHeight = Math.min(textarea.scrollHeight, 150);
    textarea.style.height = newHeight + 'px'; // Set to scrollHeight or max 150px

    if (textarea.scrollHeight > 150) {
      textarea.style.overflowY = 'auto';
    } else {
      textarea.style.overflowY = 'hidden';
    }
  };

  // Handle card selection from autocomplete
  const handleSelectCard = (cardName) => {
    const textBeforeCursor = input.substring(0, cursorPosition);
    const lastOpenBracket = textBeforeCursor.lastIndexOf('[[');
    const textAfterCursor = input.substring(cursorPosition);

    // Replace the partial card name with the selected one
    const newText =
        input.substring(0, lastOpenBracket) +
        `[[${cardName}]]` +
        textAfterCursor;

    setInput(newText);

    // Set cursos after the completed card name
    const newCursorPos = lastOpenBracket + cardName.length + 4; // 4 for [[ ]]
    setTimeout(() => {
        if (inputRef.current) {
            inputRef.current.focus();
            inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
            setCursorPosition(newCursorPos);
        }
    }, 0);
  };

  // Function that runs when user submits a message
  const handleSendMessage = async (e, messageText = null) => {
    e.preventDefault(); // Prevent page refresh

    // Increment question count
    const count = parseInt(localStorage.getItem('questionCount') || '0');
    localStorage.setItem('questionCount', (count + 1).toString());

    const textToSend = messageText || input.trim();

    // Don't send if input is empty or already loading
    if (!textToSend || loading) return;

    // Clear any previous limit errors
    setLimitError(null);

    // Create the user's message object
    const userMessage = {
      sender: 'user',
      text: textToSend
    };

    // Add user message to the chat immediately
    setMessages(prev => [...prev, userMessage]);
    
    // Clear the input field
    setInput('');
    
    // Show loading state
    setLoading(true);

    try {
      // Get Firebase auth token to send with request, but only if user is logged in
      const token = user ? await user.getIdToken() : null;

      const headers = {
        'Content-Type': 'application/json'
      };

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      // Send all messages to backend (including conversation history)
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/chat`,
        { messages: [...messages, userMessage] }, // Send full conversation
        { headers }
      );

      // Check for usage warning
      if (response.data.usageWarning) {
        setUsageWarning(response.data.usageWarning);
      } else {
        setUsageWarning(null);
      }

      // Create bot's response message object
      const botMessage = {
        sender: 'bot',
        text: response.data.reply
      };

      // Add bot message to chat
      setMessages(prev => {
        const updatedMessages = [...prev, botMessage];
        // Auto-save for premium users
        saveConversation(updatedMessages);
        return updatedMessages;
      });
      
      trackUserEvent('chat_message_sent', {
        message_length: input.trim().length,
        has_card_links: input.includes('[['),
        user_status: user ? 'logged_in' : 'anonymous',
      });

    } catch (error) {
      console.error('Error sending message:', error);

      // Handle 429 usage limit error
      if (error.response?.status === 429) {
        const errorData = error.response.data;
        setLimitError(errorData);

        // Show error message in chat
        const errorMessage = {
          sender: 'bot',
          text: `You've reached your monthly usage limit of ${errorData.details?.limit?.toLocaleString()} tokens. ${errorData.upgradeAvailable
            ? 'Upgrade to Premium for unlimited usage!'
            : `Your limit will reset in ${errorData.details?.daysUntilReset} days`
          }`
        };
        setMessages(prev => [...prev, errorMessage]);

        // Show upgrade modal for logged-in users
        if (errorData.upgradeAvailable && user) {
          setShowUpgradeModal(true);
        }

        trackUserEvent('usage_limit_reached', {
          user_status: user ? 'logged_in' : 'anonymous',
          tokens_used: errorData.details?.used,
        });
      } else {
        // Handle other errors
        Sentry.captureException(error, {
          tags: {
            component: 'Chat',
            action: 'sendMessage',
          },
          extra: {
            MessageCount: messages.length,
            hasUser: !!user,
            userMessage: input,
          },
        });

        // Show error message in chat
        const errorMessage = {
          sender: 'bot',
          text: 'Sorry, something went wrong. Please try again.'
        };
        setMessages(prev => [...prev, errorMessage]);
      }
    } finally {
      // Hide loading state
      setLoading(false);
    }
  };

  const sendMessageDirectly = async (messageText) => {
    const userMessage = {
      sender: 'user',
      text: messageText
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);
    setLimitError(null);
    
    try {
      const token = user ? await user.getIdToken() : null;
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/chat`,
        { messages: [...messages, userMessage] },
        { headers }
      );

      // Check for usage warning
      if (response.data.usageWarning) {
        setUsageWarning(response.data.usageWarning);
      } else {
        setUsageWarning(null);
      }

      const botMessage = {
        sender: 'bot',
        text: response.data.reply
      };
      setMessages(prev => {
        const updatedMessages = [...prev, botMessage];
        // Auto-save for premium users
        saveConversation(updatedMessages);
        return updatedMessages;
      });

      trackUserEvent('chat_message_sent', {
        message_length: messageText.length,
        has_card_links: messageText.includes('[['),
        user_status: user ? 'logged_in' : 'anonymous',
      });

    } catch (error) {
      console.error('Error sending message:', error);

      // Handle 429 usage limit error
      if (error.response?.status === 429) {
        const errorData = error.response.data;
        setLimitError(errorData);

        const errorMessage = {
          sender: 'bot',
          text: `You've reached your monthly usage limit. ${errorData.upgradeAvailable
            ? 'Upgrade to Premium for unlimited usage!'
            : `Your limit will reset in ${errorData.details?.daysUntilReset} days`
          }`
        };
        setMessages(prev => [...prev, errorMessage]);

        if (errorData.upgradeAvailable && user) {
          setShowUpgradeModal(true);
        }
      } else {
        const errorMessage = {
          sender: 'bot',
          text: 'Sorry, something went wrong. Please try again.'
        };
        setMessages(prev => [...prev, errorMessage]);
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle keyboard events for autocomplete
  const handleKeyDown = (e) => {
    if (window.cardAutocompleteKeyHandler) {
      const handled = window.cardAutocompleteKeyHandler(e);
        if (handled) return;
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  // Track if we're currently saving to prevent duplicate calls
  const savingRef = useRef(false);

  const saveConversation = async (updatedMessages) => {
    // Guards: only premium, not already saving, have messages
    if (!user || !userData?.isPremium) return;
    if (savingRef.current) return; // Prevent concurrent saves
    
    // Need at least 2 messages AND last message must be from bot
    if (updatedMessages.length < 2) return;
    if (updatedMessages[updatedMessages.length - 1].sender !== 'bot') return;
    
    savingRef.current = true;
    setIsSaving(true);
    
    try {
      const token = await user.getIdToken();
      
      // Convert messages to API format
      const apiMessages = updatedMessages.map(msg => ({
        role: msg.sender === 'user' ? 'user' : 'assistant',
        content: msg.text
      }));
      
      if (conversationId) {
        // Update existing conversation
        await axios.put(
          `${process.env.REACT_APP_API_URL}/api/conversations/${conversationId}`,
          { messages: apiMessages },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        // Create new conversation
        const title = updatedMessages[0]?.text?.substring(0, 50) || 'New Conversation';
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/conversations`,
          { title, messages: apiMessages },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        setConversationId(response.data._id);
        
        // Notify parent of new conversation
        if (onConversationCreated) {
          onConversationCreated(response.data._id);
        }
      }
    } catch (error) {
      console.error('Error saving conversation:', error);
      Sentry.captureException(error, {
        tags: { component: 'Chat', action: 'saveConversation' }
      });
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  };

  const handleExportPDF = () => {
    if (messages.length === 0) {
      alert('No conversation to export');
      return;
    }

    // Generate title from first user message or use conversation title
    const title = messages[0]?.text?.substring(0, 50) || 'Judgy Conversation';

    exportConversationToPDF(messages, title);

    // Track analytics
    trackUserEvent('export_pdf', {
      messages_count: messages.length,
    });
  };

  const handleDismissTip = () => {
    setShowBracketTip(false);
    localStorage.setItem('bracketTipDismissed', 'true');
  }

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100%',
      width: '100%',
      maxWidth: '800px',
      margin: '0 auto',
      overflow: 'hidden',
      flex: 1,
      ...(isMobile() && {
        maxWidth: '100%',
      })
    }}>
      {/* Usage warning banner */}
      {usageWarning && !limitError && (
        <div style={{
          backgroundColor: '#FFF8E1',
          border: '2px solid #FFE300',
          borderRadius: borderRadius.md,
          padding: spacing.lg,
          margin: `${spacing.md} ${spacing.lg}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          boxShadow: '0 2px 8px rgba(255,179,0,0.15)',
          ...(isMobile() && {
            padding: mobileSpacing.md,
            flexDirection: 'column',
            gap: mobileSpacing.sm,
          })
        }}>
          <span style={{
            color: '#5D4037',
            fontSize: typography.fontSize.base,
            fontWeight: typography.fontWeight.medium,
            ...(isMobile() && {
              fontSize: typography.mobileFontSize.xs,
              textAlign: 'center',
            })
          }}>
            ⚠️ You've used {usageWarning.percentUsed}% of your monthly limit.
          </span>
          {user && userData && !userData.isPremium && (
            <button
              onClick={() => setShowUpgradeModal(true)}
              style={mergeStyles(
                buttonStyles.base,
                buttonStyles.primary,
                buttonStyles.small,
                {
                  marginLeft: spacing.md,
                  ...(isMobile() && {
                    marginLeft: 0,
                    width: '100%',
                  })
                }
              )}
            >
              Upgrade to Premium
            </button>
          )}
        </div>
      )}

      {/* Messages display area */}
      <div style={{ 
        flex: 1,
        overflowY: messages.length === 0 ? 'hidden' : 'auto',
        padding: spacing.xl,
        backgroundColor: theme.bg.primary,
        WebkitOverflowScrolling: 'touch',
        minHeight: 0,
        ...(isMobile() && {
          padding: mobileSpacing.md,
          paddingTop: mobileSpacing.xl,
        })
      }}>
        {messages.length === 0 && !loading && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            textAlign: 'center',
            padding: spacing['3xl'],
            opacity: messages.length === 0 ? 1 : 0,
            transition: 'opacity 0.5s ease-out',
            pointerEvents: messages.length === 0 ? 'auto' : 'none',
            ...(isMobile() && {
              padding: mobileSpacing.lg,
            })
          }}>
            <div style={{
              maxWidth: '600px',
              ...(isMobile() && {
                maxWidth: '100%',
              })
            }}>
              <h2 style={{
                fontSize: typography.fontSize['3xl'],
                fontWeight: typography.fontWeight.bold,
                color: theme.text.primary,
                marginBottom: spacing.xl,
                lineHeight: typography.lineHeight.tight,
                ...(isMobile() && {
                  fontSize: typography.mobileFontSize['2xl'],
                  marginBottom: mobileSpacing.lg,
                })
              }}>
                {isMobile()
                  ? "Ask me Magic: The Gathering rules questions"
                  : "Ask me anything about Magic: The Gathering rules and card interactions"
                }
              </h2>
              <p style={{
                fontSize: typography.fontSize.base,
                marginBottom: spacing.md,
                color: theme.text.muted,
                ...(isMobile() && {
                  fontSize: typography.mobileFontSize.sm,
                  marginBottom: mobileSpacing.sm,
                })
              }}>
                Try asking:
              </p>
              <div style={{
                textAlign: 'left',
                display: 'inline-block',
                width: '100%',
                maxWidth: '400px',
              }}>
                {[
                  "How does lifelink work?",
                  "Can I target a planeswalker with [[Lightning Bolt]]?",
                  "What happens if I copy a token?"
                ].map((question, index) => (
                  <div
                    key={index}
                    style={{
                      marginBottom: spacing.sm,
                      padding: `${spacing.sm} ${spacing.md}`,
                      backgroundColor: theme.bg.secondary,
                      borderRadius: borderRadius.md,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      fontSize: typography.fontSize.base,
                      color: theme.text.secondary,
                      ...(isMobile() && {
                        fontSize: typography.mobileFontSize.sm,
                        padding: `${mobileSpacing.sm} ${mobileSpacing.md}`,
                        marginBottom: mobileSpacing.sm,
                      })
                    }}
                    onClick={() => {
                      setInput(question);
                      // Use setTimeout to ensure state updates, then submit
                      console.log('Clicked question:', question);
                      setTimeout(() => {
                        const syntheticEvent = { preventDefault: () => {} };
                        handleSendMessage(syntheticEvent);
                      }, 0);
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = theme.brand.primary;
                      e.currentTarget.style.color = theme.text.inverse;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = theme.bg.secondary;
                      e.currentTarget.style.color = theme. text.secondary;
                    }}
                  >
                    {question}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        {/* Show all messages */}
        {messages.map((msg, index) => {
          const question = msg.sender === 'bot' && index > 0
            ? messages[index - 1].text
            : '';

          return (
            <Message
                key={index}
                message={msg}
                user={user}
                question={question}
                messages={messages}
                messageIndex={index}
                userData={userData}
                conversationTitle={conversationTitle}
            />
          );
        })}
        {/* Show "typing..." indicator while waiting for bot */}
        {loading && (
          <div style={{
            textAlign: 'left',
            color: theme.text.muted,
            padding: spacing.md,
            ...(isMobile() && {
              padding: mobileSpacing.sm,
              fontSize: typography.mobileFontSize.sm,
            })
        }}>
            Bot is typing...
          </div>
        )}
        
        {/* Invisible element at bottom for auto-scroll */}
        <div ref={messagesEndRef} />
      </div>

      {/* Bracket tip banner - for new users */}
      {showBracketTip && (
        <div style={{
          backgroundColor: '#E3F2FD',
          border: `1px solid ${theme.brand.primary}`,
          borderRadius: borderRadius.md,
          padding: spacing.md,
          marginBottom: spacing.md,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: spacing.md,
          ...(isMobile() && {
            padding: mobileSpacing.sm,
            marginBottom: mobileSpacing.sm,
          })
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: spacing.md,
            flex: 1,
          }}>
            <span style={{ fontSize: typography.fontSize.xl }}>💡</span>
            <span style={{
              fontSize: typography.fontSize.sm,
              color: theme.text.primary,
              ...(isMobile() && {
                fontSize: typography.mobileFontSize.xs,
              })
            }}>
              <strong>Pro tip:</strong> Wrap card names in [[double brackets]] for more accurate answers!
              {!isMobile() && ' Example: [[Lightning Bolt]]'}
            </span>
          </div>
          <button
            onClick={handleDismissTip}
            style={{
              background: 'none',
              border: 'none',
              color: theme.text.secondary,
              cursos: 'pointer',
              fontSize: typography.fontSize.sm,
              textDecoration: 'underline',
              minWidth: '44px',
              minHeight: '44px',
              ...(isMobile() && {
                minWidth: '48px',
                minHeight: '48px',
              })
            }}
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Always-visible info icon with tooltip */}
      {!showBracketTip && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: spacing.xs,
          marginBottom: spacing.xs,
          fontSize: typography.fontSize.xs,
          color: theme.text.tertiary,
          ...(isMobile() && {
            fontSize: typography.mobileFontSize.xs,
          })
        }}>
          <span
            title="Tip: Use [[Card Name]] for best results"
            style={{ cursor: 'help' }}
          >
            💡
          </span>
          <span>Tip: Use [[Card Name]] for accuracy</span>
        </div>
      )}

      {/* Input form at bottom */}
      <form 
        onSubmit={handleSendMessage}
        style={{ 
          display: 'flex',
          padding: spacing.xl,
          borderTop: `1px solid ${theme.border.light}`,
          backgroundColor: 'white',
          position: 'relative',
          ...(isMobile() && {
            padding: mobileSpacing.md,
            flexDirection: window.innerWidth < 400 ? 'column' : 'row',
            gap: mobileSpacing.sm,
          })
        }}
      >
        {/* Saving indicator for premium users */}
        {isSaving && userData?.isPremium && (
          <div style={{
            position: 'absolute',
            top: spacing.sm,
            right: spacing.xl,
            fontSize: typography.fontSize.xs,
            color: theme.text.tertiary,
            ...(isMobile() && {
              right: mobileSpacing.md,
            })
          }}>
            Saving...
          </div>
        )}

        {/* Autocomplete dropdown */}
        <CardAutocomplete
            inputValue={input}
            cursorPosition={cursorPosition}
            onSelectCard={handleSelectCard}
        />
        <textarea
          ref={inputRef}
          value={input}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onClick={(e) => setCursorPosition(e.target.selectionStart)}
          placeholder={
            isMobile() && window.innerWidth < 400
                ? "Ask a question..."
                : "Ask a Magic: The Gathering rules question..."
          }
          rows={1}
          style={mergeStyles(
            inputStyles.base,
            {
                marginRight: spacing.md,
                flex: 1,
                resize: 'none',
                overflow: 'hidden',
                minHeight: '44px',
                maxHeight: '150px',
                fontFamily: typography.fontFamily.primary,
                lineHeight: typography.lineHeight.normal,
            },
            isMobile() && {
                ...inputStyles.baseMobile,
                marginRight: 0,
                marginBottom: window.innerWidth < 400 ? mobileSpacing.sm : 0,
                fontSize: typography.mobileFontSize.base,
            }
          )}
          disabled={loading}
        />
        <button
          type="submit"
          disabled={loading || !input.trim()}
          style={mergeStyles(
            buttonStyles.base,
            loading || !input.trim() ? buttonStyles.disabled : buttonStyles.primary,
            isMobile() && {
                ...buttonStyles.baseMobile,
                width: window.innerWidth < 400 ? '100%' : 'auto',
                padding: `${mobileSpacing.md} ${mobileSpacing.xl}`,
            }
          )}
        >
          {isMobile() && windowWidth < 400 ? '➤' : 'Send'}
        </button>
      </form>

      {/* Upgrade to Premium Modal */}
      {showUpgradeModal && user && (
        <UpgradeToPremium
          user={user}
          onClose={() => setShowUpgradeModal(false)}
          autoOpen={true}
        />
      )}
    </div>
  );
}